import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  FormControlLabel,
  FormHelperText,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import axiosInstance from "../../axios/axiosInstance";
import { acceptConsentsRoute } from "../../routes/apiRoutes";
import {
  privacyPolicyRoute,
  termsOfServiceRoute,
} from "../../routes/reactRoutes";
import { updateConsent } from "../../Redux/authenticationSlice";

/**
 * Blocking modal shown when an authenticated user has not completed
 * mandatory Terms of Service + Privacy Policy consent.
 */
const ConsentRequiredModal = ({ open, onAccepted }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    setSubmitted(true);
    if (!policiesAccepted) return;

    try {
      setLoading(true);
      const { data } = await axiosInstance.post(
        acceptConsentsRoute,
        {
          termsAccepted: true,
          privacyPolicyAccepted: true,
          source: "login_prompt",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.status === "success" || data.statusCode === 200) {
        dispatch(updateConsent(data.data));
        toast.dismiss();
        toast.success("Consent recorded successfully", { autoClose: 1500 });
        onAccepted?.(data.data);
      } else {
        toast.error("Unexpected server response", { autoClose: false });
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message || "Failed to record consent",
        { autoClose: false }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      aria-labelledby="mandatory-consent-title"
    >
      <Box
        dir={theme.direction}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography
          id="mandatory-consent-title"
          sx={{ fontSize: "1.25rem", fontWeight: 800 }}
        >
          Accept Terms & Privacy Policy
        </Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          To continue using the LAAO Management Software, please review and accept our
          policies. You can open each document using the links below.
        </Typography>

        <Box>
          <FormControlLabel
            sx={{ alignItems: "flex-start", mr: 0 }}
            control={
              <Checkbox
                checked={policiesAccepted}
                onChange={(e) => setPoliciesAccepted(e.target.checked)}
                color="primary"
                sx={{ pt: 0.25 }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14 }}>
                I have read and agree to the{" "}
                <MuiLink
                  component={Link}
                  to={termsOfServiceRoute}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </MuiLink>{" "}
                and{" "}
                <MuiLink
                  component={Link}
                  to={privacyPolicyRoute}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </MuiLink>
                , including applicable data-processing terms.
              </Typography>
            }
          />
          {submitted && !policiesAccepted && (
            <FormHelperText error>
              Please accept the Terms of Service and Privacy Policy to continue.
            </FormHelperText>
          )}
        </Box>

        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={handleAccept}
          disabled={!policiesAccepted}
          sx={{
            alignSelf: "flex-end",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1.25,
          }}
        >
          <span>Accept and Continue</span>
        </LoadingButton>
      </Box>
    </Dialog>
  );
};

export default ConsentRequiredModal;
